import { ApiResult } from 'src/Libs/api'
import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import { ShopAuthFetchWithResponse } from '../types'
import { AddressesData, PaymentMethodResponseModel, ShippingWithAvis, ShopCheckoutSideBarResponse } from '../models'
import { ShopSystemConfiguration } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationModel'
import { CountriesModelResponse, ShippingAddressForm } from '../models/new-shipping-address-modal.model'
import { CreateOrderResponseModel, CreateOrderPayload } from '../models/create-order.model'
import { handleFetchError } from 'src/Libs/api/utils'
import { ProductItem, ShopAddToCartProps } from '../models/shop-checkout-side-bar.model'
import { ShippingMethodResponseModel } from '../models/shipping-method-modal.model'
import { QntmSystemConfiguration } from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationModel'

/*
 * This endpoint returns all products of the cart
 */
export const fetchCheckoutProducts = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const response = await shopFetch<ShopCheckoutSideBarResponse>({
      path: 'checkout/cart',
      method: 'GET',
      criteria: { includes: {} },
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}

/*
 * This endpoint updates the quantity of a product in the cart by a specified number.
 */
export const updateQuantityProductToCart = async (
  productId: string,
  quantity: number,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const response = await shopFetch({
      path: 'checkout/cart/line-item',
      method: 'PATCH',
      payload: {
        items: [
          {
            id: productId,
            quantity: quantity,
          },
        ],
      },
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data as ShopCheckoutSideBarResponse)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}

/*
 * This endpoint deletes a product from the cart.
 */
export const deleteProductFromCart = async (
  productIds: string | string[],
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const ids = Array.isArray(productIds) ? productIds : [productIds]
    const response = await shopFetch({
      path: 'checkout/cart/line-item',
      method: 'DELETE',
      payload: {
        ids,
      },
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data as ShopCheckoutSideBarResponse)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}
/*
 * This endpoint adds a product to the cart
 */
const INITIAL_QUANTITY = 1
const reformatProductItem = (productId: string, quantity: number = INITIAL_QUANTITY): ProductItem => ({
  type: 'product',
  referencedId: productId,
  quantity: quantity,
})

export const addProductToCart = async (
  products: string | ShopAddToCartProps | ShopAddToCartProps[],
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  const items: { type: 'product'; referencedId: string; quantity: number }[] = []

  if (typeof products === 'string') {
    items.push(reformatProductItem(products))
    return executeShopFetch(items, shopFetch)
  }

  if (Array.isArray(products)) {
    products.forEach((product: ShopAddToCartProps) => {
      items.push(reformatProductItem(product.id, product.quantity ?? INITIAL_QUANTITY))
    })
    return executeShopFetch(items, shopFetch)
  }

  items.push(reformatProductItem(products.id, products.quantity ?? INITIAL_QUANTITY))
  return executeShopFetch(items, shopFetch)
}

const executeShopFetch = async (
  items: { type: 'product'; referencedId: string; quantity: number }[],
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  const productPayload = {
    items: items,
  }

  try {
    const response = await shopFetch({
      path: 'checkout/cart/line-item',
      method: 'POST',
      payload: productPayload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data as ShopCheckoutSideBarResponse)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}
/*
 * This endpoint adds a system configurator to the cart.
 */
export const addSystemConfigurationToCart = async (
  systemConfigurationWithUpdatedMonth: ShopSystemConfiguration | QntmSystemConfiguration,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const response = await shopFetch({
      path: 'checkout/cart/line-item',
      method: 'POST',
      payload: { items: [systemConfigurationWithUpdatedMonth] },
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data as ShopCheckoutSideBarResponse)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}

export const updateCartConfigurator = async (
  cartItemId: string,
  systemConfigurationWithUpdatedMonth: ShopSystemConfiguration | QntmSystemConfiguration,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const response = await shopFetch({
      path: 'checkout/cart/line-item',
      method: 'PATCH',
      payload: {
        items: [
          {
            id: cartItemId,
            ...systemConfigurationWithUpdatedMonth,
          },
        ],
      },
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data as ShopCheckoutSideBarResponse)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}

/*
 * This endpoint returns the payment method of the logged in user.
 */
export const fetchPaymentMethod = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<PaymentMethodResponseModel>> => {
  try {
    const response = await shopFetch<PaymentMethodResponseModel>({
      path: 'context',
      method: 'GET',
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<PaymentMethodResponseModel>(response.data)
    }

    return new ApiResult<PaymentMethodResponseModel>(null)
  } catch (error) {
    return new ApiResult<PaymentMethodResponseModel>(null)
  }
}

/*
 * This endpoint returns a list of addresses of the logged-in user.
 */
export const fetchAddresses = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<AddressesData>> => {
  try {
    const response = await shopFetch<AddressesData>({
      path: 'e3dc/addresses',
      method: 'GET',
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<AddressesData>(response.data)
    }

    return new ApiResult<AddressesData>(null)
  } catch (error) {
    return new ApiResult<AddressesData>(null)
  }
}

/*
 * This endpoint saves the shipping address of the logged-in user.
 */
export const storeShippingAddress = async (
  addressInformation: ShippingAddressForm,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShippingAddressForm>> => {
  try {
    const response = await shopFetch<ShippingAddressForm>({
      path: 'e3dc/shipping-address',
      method: 'POST',
      payload: addressInformation,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShippingAddressForm>(response.data)
    }

    return new ApiResult<ShippingAddressForm>(null)
  } catch (error) {
    return new ApiResult<ShippingAddressForm>(null)
  }
}

/*
 * This endpoint updates temporary shipping address for the next order.
 */
export const storeTemporaryShippingAddress = async (
  addressInformation: ShippingAddressForm,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShippingAddressForm>> => {
  try {
    const response = await shopFetch<ShippingAddressForm>({
      path: 'e3dc/temporary-shipping-address',
      method: 'PATCH',
      payload: addressInformation,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShippingAddressForm>(response.data)
    }

    return new ApiResult<ShippingAddressForm>(null)
  } catch (error) {
    return new ApiResult<ShippingAddressForm>(null)
  }
}

/*
 * This endpoint gets shipping methods available.
 */
export const fetchShippingMethods = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<ShippingMethodResponseModel>> => {
  try {
    const response = await shopFetch<ShippingMethodResponseModel>({
      path: 'shipping-method?onlyAvailable=true',
      method: 'GET',
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShippingMethodResponseModel>(response.data)
    }

    return new ApiResult<ShippingMethodResponseModel>(null)
  } catch (error) {
    return handleFetchError<ShippingMethodResponseModel>(error)
  }
}

/*
 * This endpoint updates shipping method.
 */
export const updateShippingMethod = async (
  shippingMethodId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<PaymentMethodResponseModel>> => {
  try {
    const response = await shopFetch<PaymentMethodResponseModel>({
      path: 'context',
      method: 'PATCH',
      payload: { shippingMethodId: shippingMethodId },
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<PaymentMethodResponseModel>(response.data)
    }

    return new ApiResult<PaymentMethodResponseModel>(null)
  } catch (error) {
    return handleFetchError<PaymentMethodResponseModel>(error)
  }
}

/*
 * This endpoint returns a list of countries that are allowed on the shop
 */
export const fetchCountries = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<CountriesModelResponse>> => {
  try {
    const payload = {
      includes: {
        dal_entity_search_result: ['elements'],
        country: ['iso', 'name', 'position', 'translated'],
        translated: ['name'],
      },
    }
    const response = await shopFetch<CountriesModelResponse>({
      path: 'country',
      method: 'GET',
      criteria: payload,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<CountriesModelResponse>(response.data)
    }

    return new ApiResult<CountriesModelResponse>(null)
  } catch (error) {
    return new ApiResult<CountriesModelResponse>(null)
  }
}

/*
 *This endpoint updates the desired shipping date for the product
 */
export const updateDesiredShippingDate = async (
  day: number,
  month: number,
  year: number,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const payload = {
      day,
      month,
      year,
    }
    const response = await shopFetch<ShopCheckoutSideBarResponse>({
      path: 'e3dc/cart/desired-shipping-date',
      method: 'POST',
      payload: payload,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}

/*
 *This endpoint creates an order.
 */
export const requestOrder = async (
  billingAddressName: string,
  shopFetch: ShopAuthFetchWithResponse,
  shippingAddressName?: string | null,
  customerReferenceNumber?: string | null,
  customerComment?: string | null,
  avisInfo?: ShippingWithAvis | null,
  usePortalToken?: boolean
): Promise<ApiResult<CreateOrderResponseModel>> => {
  const payload: CreateOrderPayload = {
    billingAddressName,
  }
  if (avisInfo) {
    payload.avisInfo = {
      contactPerson: avisInfo.contactPerson,
      phoneNumber: avisInfo.phoneNumber,
      comment: avisInfo.comment,
    }
  }
  if (customerReferenceNumber) payload.customerReferenceNumber = customerReferenceNumber
  if (customerComment) payload.customerComment = customerComment
  if (shippingAddressName) payload.shippingAddressName = shippingAddressName

  const response = await shopFetch<CreateOrderResponseModel>({
    path: 'checkout/order',
    method: 'POST',
    payload: payload,
    usePortalToken,
  })
  if (response.status === StatusCodes.OK) {
    return new ApiResult<CreateOrderResponseModel>(response.data)
  }

  return new ApiResult<CreateOrderResponseModel>(null)
}

/*
 * This endpoint add Retrofit Battery Into Cart.
 */
export const addRetrofitBatteryToCart = async (
  productId: string,
  quantity: number,
  serialNumber: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const response = await shopFetch({
      path: 'checkout/cart/line-item',
      method: 'POST',
      payload: {
        items: [
          {
            type: 'product',
            referencedId: productId,
            quantity,
            payload: {
              serialNumber,
            },
          },
        ],
      },
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data as ShopCheckoutSideBarResponse)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null, true)
  } catch (error) {
    return handleFetchError<ShopCheckoutSideBarResponse>(error)
  }
}

/*
 * This endpoint can add promotion code into the cart.
 */
export const addPromotionCodeToCart = async (code: string, shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const response = await shopFetch({
      path: 'checkout/cart/line-item',
      method: 'POST',
      payload: {
        items: [
          {
            type: 'promotion',
            referencedId: code,
          },
        ],
      },
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data as ShopCheckoutSideBarResponse)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null, true)
  } catch (error) {
    return handleFetchError<ShopCheckoutSideBarResponse>(error)
  }
}
